import type {
  AssignUserInput,
  ListAssignedUsersResponse,
  ListLocationsForUserResponse,
  LocationAssignmentResponse,
  MyLocationsResponse,
} from '@jq4race/shared';
import { getBaseUrl, getCurrentAccessToken, type RequestOptions, request } from './client.js';
import { ApiError } from './errors.js';

/**
 * Assign a user to a location.
 *
 * Returns `{ data, created }`:
 * - `created: true`  → HTTP 201, new assignment was created.
 * - `created: false` → HTTP 200, assignment already existed (idempotent re-assign).
 */
export async function assignUserToLocation(
  locationId: string,
  input: AssignUserInput,
): Promise<{ data: LocationAssignmentResponse; created: boolean }> {
  const { body, status } = await _fetchWithStatus<LocationAssignmentResponse>(
    `/admin/locations/${encodeURIComponent(locationId)}/assignments`,
    { method: 'POST', body: input },
  );
  return { data: body, created: status === 201 };
}

export async function unassignUserFromLocation(locationId: string, userId: string): Promise<void> {
  return request<void>(
    `/admin/locations/${encodeURIComponent(locationId)}/assignments/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}

export async function listAssignedUsers(
  locationId: string,
  params?: { page?: number; limit?: number },
): Promise<ListAssignedUsersResponse> {
  const qs = new URLSearchParams();
  if (params?.page !== undefined) qs.set('page', String(params.page));
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return request<ListAssignedUsersResponse>(
    `/admin/locations/${encodeURIComponent(locationId)}/assignments${query}`,
    { method: 'GET' },
  );
}

export async function listLocationsForUser(userId: string): Promise<ListLocationsForUserResponse> {
  return request<ListLocationsForUserResponse>(
    `/admin/users/${encodeURIComponent(userId)}/locations`,
    { method: 'GET' },
  );
}

export async function getMyLocations(): Promise<MyLocationsResponse> {
  return request<MyLocationsResponse>('/me/locations', { method: 'GET' });
}

// ---------------------------------------------------------------------------
// Internal helper: raw fetch that exposes the HTTP status code.
// Used only for assignUserToLocation (needs 200 vs 201 discrimination).
// ---------------------------------------------------------------------------

type _RawResult<T> = { status: number; body: T };

async function _fetchWithStatus<T>(path: string, opts: RequestOptions): Promise<_RawResult<T>> {
  const token = getCurrentAccessToken();
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;

  const init: RequestInit = {
    method: opts.method ?? 'POST',
    headers,
    credentials: 'include',
    signal: opts.signal,
  };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  let res: Response;
  try {
    res = await fetch(`${getBaseUrl()}${path}`, init);
  } catch (err) {
    throw new ApiError('network_error', err instanceof Error ? err.message : 'fetch failed');
  }

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const serverCode = (payload as { code?: string } | null)?.code;
    const code = serverCode ?? 'unknown';
    const message = (payload as { message?: string } | null)?.message ?? `HTTP ${res.status}`;
    throw new ApiError(code as never, message, res.status, payload, serverCode === undefined);
  }

  return { status: res.status, body: payload as T };
}
